const SUPPORTED_PACK_VERSION = 1;
const DEFAULT_SKIN = "default";

export async function loadSkin() {
    const requested = readRequestedSkin();
    const resolved = await resolveSkin(requested);

    let manifest = await tryFetchManifest(resolved);

    if (!manifest && resolved !== DEFAULT_SKIN) {
        console.warn(`[skin] manifest.json not found for "${resolved}", falling back to default`);
        manifest = await tryFetchManifest(DEFAULT_SKIN);
    }

    if (!manifest) {
        throw new Error("Cannot load skin manifest (default missing).");
    }

    if (manifest.packVersion !== SUPPORTED_PACK_VERSION) {
        throw new Error(
            `Skin "${manifest.name ?? resolved}" packVersion ${manifest.packVersion} ` +
            `is not supported (expected ${SUPPORTED_PACK_VERSION}). Update the game build.`,
        );
    }

    if (!Array.isArray(manifest.bundles)) {
        throw new Error(`Skin "${resolved}" manifest is malformed: bundles is not an array.`);
    }

    return { name: resolved, manifest: normalizeManifest(manifest, resolved) };
}

function readRequestedSkin() {
    try {
        const params = new URLSearchParams(location.search);
        const param = params.get("s") ?? params.get("skin");
        return param && param.trim() !== "" ? param.trim() : DEFAULT_SKIN;
    } catch {
        return DEFAULT_SKIN;
    }
}

async function resolveSkin(requested) {
    try {
        const res = await fetch("/skins/index.json");
        if (!res.ok) {
            console.error(`[skin] index.json fetch failed: ${res.status}`);
            return DEFAULT_SKIN;
        }
        const data = await res.json();
        const list = Array.isArray(data?.skins) ? data.skins : [];
        if (list.includes(requested)) return requested;
        if (requested !== DEFAULT_SKIN) {
            console.warn(`[skin] "${requested}" not in index.json, falling back to default`);
        }
        return DEFAULT_SKIN;
    } catch (err) {
        console.error("[skin] index.json error:", err);
        return DEFAULT_SKIN;
    }
}

async function tryFetchManifest(skin) {
    try {
        const res = await fetch(`/skins/${skin}/manifest.json`);
        if (!res.ok) return null;
        return await res.json();
    } catch (err) {
        console.error(`[skin] manifest.json error for "${skin}":`, err);
        return null;
    }
}

function normalizeManifest(manifest, skin) {
    return {
        ...manifest,
        bundles: manifest.bundles.map((bundle) => ({
            ...bundle,
            assets: bundle.assets.map((asset) => ({
                ...asset,
                src: rewriteSrc(asset.src, skin),
            })),
        })),
    };
}

function rewriteSrc(src, skin) {
    if (Array.isArray(src)) return src.map((s) => rewriteSrc(s, skin));

    if (/^(?:https?:)?\/\//.test(src) || src.startsWith("data:") || src.startsWith("blob:")) {
        return src;
    }
    if (src.startsWith("/")) return src;

    const trimmed = src.replace(/^\.\//, "");
    return `/skins/${skin}/${trimmed}`;
}
